import { getCsrfToken } from '../utils/auth';

// The Software BOM's routes (`/api/bom/sbom/*`, sbom_api.py). Kept apart from the hardware BOM
// client (bom/api/orcanosClient.js) and its synchronous settings store on purpose: the two
// parts of the module share a look, not state (design/DESIGN_SBOM.md §4).

export class SbomError extends Error {
  constructor(status, message) {
    super(message || `Request failed (HTTP ${status})`);
    this.httpCode = status;
  }
}

async function call(path, { method = 'GET', body, form } = {}) {
  const headers = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-CSRF-Token'] = getCsrfToken();
  let payload;
  if (form) payload = form;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let resp;
  try {
    resp = await fetch(`/api/bom/sbom${path}`, { method, headers, body: payload, credentials: 'same-origin' });
  } catch (e) {
    throw new SbomError(0, 'Could not reach the server. Check your connection and try again.');
  }
  let data = null;
  try { data = await resp.json(); } catch { /* not JSON */ }
  if (!resp.ok) {
    const d = data && data.detail;
    throw new SbomError(resp.status, typeof d === 'string' ? d : `Request failed (HTTP ${resp.status})`);
  }
  return data;
}

const APP_URL = () => window.location.origin + (process.env.PUBLIC_URL || '');

/** Does a finding fall under the Orcanos rule? The same test as `sbom_api.rule_matches`. */
export function ruleMatches(rule, f) {
  if (!rule) return false;
  if (rule.release && !(f.releases || []).some((r) => String(r.item_id) === String(rule.release))) return false;
  if (rule.verdicts?.length && !rule.verdicts.includes(f.state)) return false;
  if (rule.kev === 'only' && !f.kev) return false;
  const sevOk = !rule.severities?.length || rule.severities.includes(f.severity);
  return sevOk || (rule.kev === 'also' && !!f.kev);
}

const qs = (params) => {
  const p = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return p.length ? `?${p.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}` : '';
};

export const sbomApi = {
  // SBOM Dashboard + SBOM AI Studio (v4.32.0) — sbom_dashboard_api.py, sbom_studio.py.
  dashboardHome: () => call('/dashboard'),
  // Traceability panels whose level 1 is the vulnerability type, shown on the home (sbom_dashboard_api.py).
  dashPanels: () => call('/dashboard/panels'),
  dashPanelCandidates: () => call('/dashboard/panels/candidates'),
  addDashPanel: (setupId) => call('/dashboard/panels', { method: 'POST', body: { setup_id: setupId } }),
  removeDashPanel: (setupId) => call(`/dashboard/panels/${encodeURIComponent(setupId)}`, { method: 'DELETE' }),
  dashboard: (rid) => call(`/dashboard/${encodeURIComponent(rid)}`),
  dashboardRows: (rid, params) => call(`/dashboard/${encodeURIComponent(rid)}/rows${qs(params)}`),
  dashBaselines: (rid) => call(`/dashboard/${encodeURIComponent(rid)}/baselines`),
  takeDashBaseline: (rid, body) => call(`/dashboard/${encodeURIComponent(rid)}/baselines`, { method: 'POST', body }),
  archiveDashBaseline: (rid, bid, archived = true) =>
    call(`/dashboard/${encodeURIComponent(rid)}/baselines/${encodeURIComponent(bid)}/archive?archived=${archived ? 'true' : 'false'}`, { method: 'POST' }),
  dashCompare: (rid, a, b = 'now') => call(`/dashboard/${encodeURIComponent(rid)}/compare${qs({ a, b })}`),
  dashAnalyse: (rid, body) => call(`/dashboard/${encodeURIComponent(rid)}/compare/analysis`, { method: 'POST', body }),
  studio: (release = '') => call(`/studio${qs({ release })}`),
  studioUpload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return call('/studio/files', { method: 'POST', form });
  },
  studioFile: (id, enabled) => call(`/studio/files/${encodeURIComponent(id)}`, { method: 'PATCH', body: { enabled } }),
  studioDeleteFile: (id) => call(`/studio/files/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  studioRun: (body) => call('/studio/runs', { method: 'POST', body }),
  studioGetRun: (id) => call(`/studio/runs/${encodeURIComponent(id)}`),
  studioCancel: (id) => call(`/studio/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  studioDeleteRun: (id) => call(`/studio/runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  studioApply: (id, rowIds) => call(`/studio/runs/${encodeURIComponent(id)}/apply`, { method: 'POST', body: { row_ids: rowIds } }),
  studioReject: (id, rowIds) => call(`/studio/runs/${encodeURIComponent(id)}/reject`, { method: 'POST', body: { row_ids: rowIds } }),

  settings: () => call('/settings'),
  saveSettings: (values) => call('/settings', { method: 'PUT', body: values }),
  formFields: (which = 'component') => call(`/form-fields?which=${encodeURIComponent(which)}`),
  releases: () => call('/releases'),
  // `live` overlays the Component filter's current values (one Orcanos call); Expand all skips it.
  children: (releaseItemId, parentItemId = '', live = true) =>
    call('/children', { method: 'POST', body: { release_item_id: String(releaseItemId), parent_item_id: String(parentItemId || ''), live } }),
  components: (q = '', page = 1) => call(`/components?q=${encodeURIComponent(q)}&page=${page}`),
  packages: (q = '', page = 1) => call(`/packages?q=${encodeURIComponent(q)}&page=${page}`),
  whereUsed: (itemId, allVersions = true) => call(`/where-used?item_id=${encodeURIComponent(itemId)}&all_versions=${allVersions ? 1 : 0}`),
  traceRelease: (itemId) => call(`/releases/${encodeURIComponent(itemId)}/trace`, { method: 'POST' }),
  traceStatus: (itemId) => call(`/releases/${encodeURIComponent(itemId)}/trace`),
  deleteRelease: (itemId, confirm) => call(`/releases/${encodeURIComponent(itemId)}/delete`, { method: 'POST', body: { confirm } }),
  compare: (a, b) => call(`/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`),
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return call('/imports', { method: 'POST', form });
  },
  imports: () => call('/imports'),
  getImport: (id) => call(`/imports/${id}`),
  setLayout: (id, sheet, headerRow) => call(`/imports/${id}/layout`, { method: 'PUT', body: { sheet, header_row: headerRow } }),
  setMapping: (id, mapping) => call(`/imports/${id}/mapping`, { method: 'PUT', body: { mapping } }),
  setTarget: (id, target) => call(`/imports/${id}/target`, { method: 'PUT', body: target }),
  // app_url: where this app lives as the browser shows it (an IIS sub-path included), for the SBOM link field.
  create: (id) => call(`/imports/${id}/create`, { method: 'POST', body: { app_url: window.location.origin + (process.env.PUBLIC_URL || '') } }),
  locate: (key) => call(`/components/locate?key=${encodeURIComponent(key)}`),
  deleteImport: (id) => call(`/imports/${id}`, { method: 'DELETE' }),
  reopen: (id) => call(`/imports/${id}/reopen`, { method: 'POST' }),
  reload: (id) => call(`/imports/${id}/reload`, { method: 'POST' }),
  stop: (id) => call(`/imports/${id}/stop`, { method: 'POST' }),
  // Vulnerabilities (Phase 2) and the risk link (Phase 3).
  vulnStatus: () => call('/vulns/status'),
  scan: () => call('/vulns/scan', { method: 'POST' }),
  findings: ({ view = 'all', q = '', release = '' } = {}) =>
    call(`/findings?view=${encodeURIComponent(view)}&q=${encodeURIComponent(q)}&release=${encodeURIComponent(release)}`),
  // Check history: every finished check with its findings; a saved one is kept for good.
  checks: (saved = false) => call(`/vulns/checks${saved ? '?saved=true' : ''}`),
  check: (id, { view = 'all', q = '', release = '' } = {}) =>
    call(`/vulns/checks/${encodeURIComponent(id)}?view=${encodeURIComponent(view)}&q=${encodeURIComponent(q)}&release=${encodeURIComponent(release)}`),
  saveCheck: (id, name, note = '') => call(`/vulns/checks/${encodeURIComponent(id)}/save`, { method: 'PUT', body: { name, note } }),
  unsaveCheck: (id) => call(`/vulns/checks/${encodeURIComponent(id)}/save`, { method: 'DELETE' }),
  finding: (vulnId, pkg) => call(`/findings/detail?vuln_id=${encodeURIComponent(vulnId)}&package=${encodeURIComponent(pkg)}`),
  assess: (body) => call('/assessments', { method: 'PUT', body: { ...body, app_url: APP_URL() } }),
  // Vulnerabilities → Orcanos Vulnerability items: the saved rule, a preview, the batch and its job.
  orcanos: () => call('/vulns/orcanos'),
  orcanosPreview: (rule, update = true) => call('/vulns/orcanos/preview', { method: 'POST', body: { rule, update } }),
  orcanosRule: (rule) => call('/vulns/orcanos/rule', { method: 'PUT', body: rule }),
  orcanosWrite: (rule, update = true) => call('/vulns/orcanos/write', { method: 'POST', body: { rule, update, app_url: APP_URL() } }),
  orcanosStop: () => call('/vulns/orcanos/stop', { method: 'POST' }),
  risks: (vulnId = '', pkg = '', q = '') =>
    call(`/risks?vuln_id=${encodeURIComponent(vulnId)}&package=${encodeURIComponent(pkg)}&q=${encodeURIComponent(q)}`),
  riskChain: (itemId) => call(`/risks/${encodeURIComponent(itemId)}`),
  riskAi: (vulnId, pkg, force = false) => call('/risks/ai', { method: 'POST', body: { vuln_id: vulnId, package: pkg, force } }),
  // The component page: our record (fast) and the live Orcanos form (its own call, so the page never waits on it).
  componentDetail: (itemId) => call(`/components/${encodeURIComponent(itemId)}/detail`),
  componentForm: (itemId) => call(`/components/${encodeURIComponent(itemId)}/form`),
};

/** Download one vulnerability's assessment as a PDF (fetch + blob, Critical Note #85). */
export async function downloadAssessmentPdf(vulnId, pkg) {
  const resp = await fetch(`/api/bom/sbom/findings/detail/pdf?vuln_id=${encodeURIComponent(vulnId)}&package=${encodeURIComponent(pkg)}`,
    { credentials: 'same-origin' });
  if (!resp.ok) {
    let msg = `PDF failed (HTTP ${resp.status})`;
    try { const d = await resp.json(); if (typeof d.detail === 'string') msg = d.detail; } catch { /* keep */ }
    throw new SbomError(resp.status, msg);
  }
  const m = (resp.headers.get('content-disposition') || '').match(/filename="([^"]+)"/);
  const name = m ? m[1] : `${vulnId} assessment.pdf`;
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return name;
}

/** Download a release's SBOM. A fetch + blob (never an <a href> to /api — the dev proxy answers a
 *  navigation with the app, Critical Note #85). */
export async function downloadExport(releaseItemId, format, vex = true) {
  const resp = await fetch(`/api/bom/sbom/releases/${encodeURIComponent(releaseItemId)}/export?format=${encodeURIComponent(format)}&vex=${vex ? 1 : 0}`,
    { credentials: 'same-origin' });
  if (!resp.ok) {
    let msg = `Export failed (HTTP ${resp.status})`;
    try { const d = await resp.json(); if (typeof d.detail === 'string') msg = d.detail; } catch { /* keep */ }
    throw new SbomError(resp.status, msg);
  }
  const cd = resp.headers.get('content-disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const name = m ? m[1] : `sbom.${format}`;
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return name;
}

/** Download one component's report PDF (a fetch + blob, like downloadExport — Note #85). */
export async function downloadComponentPdf(itemId) {
  const resp = await fetch(`/api/bom/sbom/components/${encodeURIComponent(itemId)}/report.pdf`, { credentials: 'same-origin' });
  if (!resp.ok) {
    let msg = `Export failed (HTTP ${resp.status})`;
    try { const d = await resp.json(); if (typeof d.detail === 'string') msg = d.detail; } catch { /* keep */ }
    throw new SbomError(resp.status, msg);
  }
  const cd = resp.headers.get('content-disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const name = m ? decodeURIComponent(m[1]) : 'component.pdf';
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return name;
}

/** The Orcanos web link for an item of the SBOM project. */
export function itemUrl(baseUrl, versionId, typeCode, itemId) {
  if (!baseUrl || !versionId || !itemId) return '';
  return `${baseUrl}web/${versionId}/items/view?Item=${encodeURIComponent(typeCode)}&ItemId=${encodeURIComponent(itemId)}`;
}
